import apiClient from './client';

export type ExamKind = 'mock' | 'retake' | 'wrong' | 'marathon' | 'ticket' | 'topic';

export interface ExamAttemptListItem {
  id: string;
  user_id: string;
  kind: ExamKind;
  ref_id: number | null;
  mode: string | null;
  started_at: string;
  finished_at: string | null;
  total_questions: number;
  answered_count: number;
  correct_count: number;
  wrong_count: number;
  time_spent_seconds: number | null;
  passed: boolean | null;
  abandoned: boolean;
  created_at: string;
}

export interface ExamAttemptDetail extends ExamAttemptListItem {
  answers_log: Array<{
    question_id: number;
    selected: string;
    correct: string;
    is_correct: boolean;
    ms_taken?: number;
  }>;
  client_meta: Record<string, unknown> | null;
  updated_at: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
}

export interface ListAttemptsParams {
  page?: number;
  pageSize?: number;
  kind?: ExamKind;
  userId?: string;
  from?: string;
  to?: string;
  passed?: 'true' | 'false';
}

export interface WrongAnswerRow {
  id: string;
  user_id: string;
  question_id: number;
  wrong_count: number;
  last_wrong_at: string;
  last_session_kind: string | null;
  created_at: string;
}

export interface WrongHeatmapRow {
  question_id: number;
  total_wrong: number;
  users_affected: number;
  last_wrong_at: string;
}

export interface UserActivitySummary {
  recent_attempts: ExamAttemptListItem[];
  attempts_total: number;
  wrong_total: number;
  wrong_unique_questions: number;
  favourites_count: number;
}

export interface UserFavouriteRow {
  id: string;
  user_id: string;
  question_id: number;
  added_at: string;
  created_at: string;
}

export const learnerApi = {
  async listAttempts(params: ListAttemptsParams): Promise<PaginatedResponse<ExamAttemptListItem>> {
    const res = await apiClient.get('/admin/learner/exam-attempts', { params });
    return res.data;
  },

  async getAttempt(id: string): Promise<ExamAttemptDetail> {
    const res = await apiClient.get(`/admin/learner/exam-attempts/${id}`);
    return res.data;
  },

  async listWrongAnswers(params: {
    page?: number;
    pageSize?: number;
    userId?: string;
    questionId?: number;
    minWrongCount?: number;
  }): Promise<PaginatedResponse<WrongAnswerRow>> {
    const res = await apiClient.get('/admin/learner/wrong-answers', { params });
    return res.data;
  },

  async wrongHeatmap(limit = 50): Promise<{ items: WrongHeatmapRow[] }> {
    const res = await apiClient.get('/admin/learner/wrong-answers/heatmap', { params: { limit } });
    return res.data;
  },

  async userActivity(userId: string): Promise<UserActivitySummary> {
    const res = await apiClient.get(`/admin/learner/users/${userId}/activity`);
    return res.data;
  },

  async userFavourites(userId: string): Promise<{ items: UserFavouriteRow[]; total: number }> {
    const res = await apiClient.get(`/admin/learner/users/${userId}/favourites`);
    return res.data;
  },
};

// ─── Admin Audit ──────────────────────────────────────────────────────────────
export interface AuditLogRow {
  id: string;
  admin_id: string | null;
  admin_username: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  payload: Record<string, unknown> | null;
  status_code: number;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export const auditApi = {
  async list(params: {
    page?: number;
    pageSize?: number;
    adminId?: string;
    action?: string;
    targetType?: string;
    from?: string;
    to?: string;
  }): Promise<PaginatedResponse<AuditLogRow>> {
    const res = await apiClient.get('/admin/audit-log', { params });
    return res.data;
  },
};
